import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSave, faTags } from "@fortawesome/free-solid-svg-icons";
import { UI_COLORS, colorHelpers } from "../../utils/colors";
import { extractApiError } from "../../utils/apiError";

/**
 * ProductCategoryForm Component
 * Form for creating/editing product categories
 */
const ProductCategoryForm = ({
  category = null,
  onSubmit,
  onCancel, // legacy
  onClose, // preferred
  isOpen = true, // modal visibility
  embedded = false, // when true, render only the inner panel (no overlay)
  loading = false,
}) => {
  const { t } = useTranslation();
  const en = {
    productCategories: t("productCategories", { returnObjects: true }),
    common: t("common", { returnObjects: true }),
  };

  // prefer onClose if provided, otherwise fall back to onCancel for backward compatibility
  const handleClose = onClose || onCancel || (() => { });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  // Local submit state: close only on success (the hook returns {success:false}
  // on failure rather than throwing, so the old catch-only close was wrong).
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  /**
   * Initialize form data when category changes
   */
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
      });
    } else {
      setFormData({
        name: "",
      });
    }
    setErrors({});
    setTouched({});
  }, [category]);

  /**
   * Handle input changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  /**
   * Handle input blur
   */
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    validateField(name, formData[name]);
  };

  /**
   * Validate individual field
   */
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "name":
        if (!value || !value.trim()) {
          error = en.productCategories.validation.nameRequired;
        } else if (value.trim().length < 2) {
          error = en.productCategories.validation.nameMinLength;
        } else if (value.trim().length > 100) {
          error = en.productCategories.validation.nameMaxLength;
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    return !error;
  };

  /**
   * Validate entire form
   */
  const validateForm = () => {
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      if (!validateField(key, formData[key])) {
        isValid = false;
      }
    });

    return isValid;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;            // guard double-submit

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Prepare submission data
    const submitData = {
      name: formData.name.trim(),
    };

    setSubmitError('');
    setSubmitting(true);
    try {
      const result = await onSubmit(submitData);
      // Close ONLY on genuine success. The hook resolves with {success:false}
      // on failure (it doesn't throw), so closing on any resolved value dropped
      // the entry on error.
      if (result && result.success) {
        handleClose();
      } else {
        const rawErr = result && result.error;
        const msg = typeof rawErr === 'string' ? rawErr : (rawErr ? extractApiError(rawErr) : '');
        setSubmitError(msg || 'Could not save. Please try again.');
      }
    } catch (error) {
      setSubmitError(extractApiError(error) || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  if (!isOpen) return null;
  // Prepare the inner panel (so it can be rendered embedded inside a parent modal)
  const panel = (
    <div
      className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto product-category-form-modal"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-6 border-b"
        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
      >
        <h2
          className="text-2xl font-bold"
          style={{ color: UI_COLORS.TEXT.PRIMARY }}
        >
          {category
            ? en.productCategories.form.editTitle
            : en.productCategories.form.addTitle}
        </h2>
        <button
          onClick={handleClose}
          className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
          style={{
            color: UI_COLORS.TEXT.MUTED,
            backgroundColor: colorHelpers.withOpacity(
              UI_COLORS.TEXT.MUTED,
              0.1
            ),
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = colorHelpers.withOpacity(
              UI_COLORS.TEXT.MUTED,
              0.2
            );
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = colorHelpers.withOpacity(
              UI_COLORS.TEXT.MUTED,
              0.1
            );
          }}
        >
          <FontAwesomeIcon icon={faTimes} className="text-lg" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Category Name */}
        <div className="space-y-2">
          <label
            htmlFor="name"
            className="block text-sm font-semibold"
            style={{ color: UI_COLORS.FORMS.LABEL }}
          >
            <FontAwesomeIcon icon={faTags} className="mr-2" />
            {en.productCategories.form.name}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
            style={{
              backgroundColor: UI_COLORS.FORMS.INPUT.background,
              borderColor:
                errors.name && touched.name
                  ? UI_COLORS.STATUS.ERROR
                  : UI_COLORS.FORMS.INPUT.border,
              color: UI_COLORS.FORMS.INPUT.text,
              ...(errors.name &&
                touched.name && {
                backgroundColor: colorHelpers.withOpacity(
                  UI_COLORS.STATUS.ERROR,
                  0.1
                ),
              }),
            }}
            placeholder={en.productCategories.form.namePlaceholder}
            disabled={submitting}
          />
          {errors.name && touched.name && (
            <p
              className="text-sm font-medium"
              style={{ color: UI_COLORS.STATUS.ERROR }}
            >
              {errors.name}
            </p>
          )}
        </div>

        {/* (Taxes removed) */}

        {submitError && (
          <div
            className="rounded-xl px-4 py-3 text-sm font-medium mt-4"
            role="alert"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
              color: UI_COLORS.STATUS.ERROR,
              border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
            }}
          >
            {submitError}
          </div>
        )}

        {/* Form Actions — key off local `submitting` (reset in finally). */}
        <div className="flex items-center justify-end space-x-4 pt-6">
          {/* Cancel - text-only button, visible color */}
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50"
            style={{
              backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
              color: UI_COLORS.BUTTONS.SECONDARY.text,
              border: `2px solid ${UI_COLORS.BUTTONS.SECONDARY.border}`,
            }}
          >
            {en.common.buttons?.cancel || en.common.buttons?.close}
          </button>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
              color: UI_COLORS.BUTTONS.PRIMARY.text,
              border: `2px solid ${UI_COLORS.BUTTONS.PRIMARY.background}`,
            }}
          >
            {submitting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>
                  {category
                    ? en.productCategories.form.loadingUpdate
                    : en.productCategories.form.loadingCreate}
                </span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faSave} />
                <span>
                  {category ? en.productCategories.form.editTitle : en.productCategories.actions.add}
                </span>
              </div>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  // If embedded, render only the inner panel (used when parent page provides overlay)
  if (embedded) return panel;

  // Otherwise render the overlay + panel
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.product-category-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .product-category-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      {panel}
    </div>
  );
};

export default ProductCategoryForm;
